import { runtimeEnv } from '@/config/featureFlags';
import { trackEvent } from '../analyticsService';
import { AUTH_EVENTS } from '../events';
import { getNavigationTrigger } from '../navigationTrigger';

const VALID_PROVIDERS = Object.freeze(['google', 'twitter', 'discord', 'google_one_tap']);

const withAuthDefaults = (payload = {}) => {
  const result = { module_name: 'auth' };

  const map = (camel, snake) => {
    if (payload[camel] !== undefined) result[snake] = payload[camel];
    if (payload[snake] !== undefined) result[snake] = payload[snake];
  };

  map('eventLabel', 'event_label');
  map('sourcePosition', 'source_position');
  map('elementName', 'element_name');
  map('elementType', 'element_type');
  map('authFlow', 'auth_flow');
  map('method', 'method');
  map('actionStatus', 'action_status');
  map('reasonCode', 'reason_code');
  map('errorCode', 'error_code');
  map('errorMessage', 'error_message');
  map('redirectTarget', 'redirect_target');
  map('redirectPresent', 'redirect_present');
  map('durationMs', 'duration_ms');
  map('sessionSource', 'session_source');
  map('isLoggedIn', 'is_logged_in');
  map('lang', 'lang');
  map('menuState', 'menu_state');
  map('httpStatus', 'http_status');
  map('emailDomain', 'email_domain');
  map('isBusinessEmail', 'is_business_email');

  return result;
};

export function useAuthAnalytics() {
  const trackAuthEntryClicked = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_ENTRY_CLICKED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'entry_clicked'
    });
  };

  const trackAuthPageViewed = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_PAGE_VIEWED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'page_viewed',
      navigation_trigger: getNavigationTrigger()
    });
  };

  const trackAuthModeSwitched = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_MODE_SWITCHED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'mode_switched',
      auth_flow: payload.authFlow || payload.auth_flow
    });
  };

  const trackAuthSubmitStarted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_SUBMIT_STARTED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'submit_started',
      auth_flow: payload.authFlow || payload.auth_flow,
      method: payload.method || 'email'
    });
  };

  const trackAuthSubmitCompleted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_SUBMIT_COMPLETED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'submit_completed',
      auth_flow: payload.authFlow || payload.auth_flow,
      method: payload.method || 'email',
      action_status: payload.actionStatus || payload.action_status
    });
  };

  const trackAuthOauthStarted = (payload = {}) => {
    const method = payload.method;
    if (method && !VALID_PROVIDERS.includes(method)) {
      if (runtimeEnv.isDevelopment) {
        console.warn(`[Analytics] Unknown OAuth provider: ${method}`);
      }
      return;
    }

    trackEvent(AUTH_EVENTS.AUTH_OAUTH_STARTED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'oauth_started',
      auth_flow: payload.authFlow || payload.auth_flow || 'oauth_login',
      method: payload.method
    });
  };

  const trackAuthOauthRedirectStarted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_OAUTH_REDIRECT_STARTED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'oauth_redirect_started',
      auth_flow: payload.authFlow || payload.auth_flow || 'oauth_login',
      method: payload.method,
      action_status: payload.actionStatus || payload.action_status
    });
  };

  const trackAuthOauthCallbackCompleted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_OAUTH_CALLBACK_COMPLETED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'oauth_callback_completed',
      auth_flow: payload.authFlow || payload.auth_flow || 'oauth_login',
      method: payload.method
    });
  };

  const trackAuthEmailConfirmationPromptViewed = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_EMAIL_CONFIRMATION_PROMPT_VIEWED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'confirmation_prompt_viewed'
    });
  };

  const trackAuthConfirmationEmailResendStarted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_CONFIRMATION_EMAIL_RESEND_STARTED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'resend_started',
      auth_flow: payload.authFlow || payload.auth_flow,
      method: payload.method || 'email'
    });
  };

  const trackAuthConfirmationEmailResendCompleted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_CONFIRMATION_EMAIL_RESEND_COMPLETED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'resend_completed',
      auth_flow: payload.authFlow || payload.auth_flow,
      method: payload.method || 'email',
      action_status: payload.actionStatus || payload.action_status
    });
  };

  const trackAuthRedirectCompleted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_REDIRECT_COMPLETED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'redirect_completed',
      action_status: payload.actionStatus || payload.action_status
    });
  };

  const trackAuthSessionCheckCompleted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_SESSION_CHECK_COMPLETED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'session_check_completed',
      action_status: payload.actionStatus || payload.action_status
    });
  };

  const trackAuthLogoutStarted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_LOGOUT_STARTED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'logout_started',
      auth_flow: payload.authFlow || payload.auth_flow || 'logout'
    });
  };

  const trackAuthLogoutCompleted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_LOGOUT_COMPLETED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'logout_completed',
      auth_flow: payload.authFlow || payload.auth_flow || 'logout',
      action_status: payload.actionStatus || payload.action_status
    });
  };

  const trackAuthUserMenuToggled = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_USER_MENU_TOGGLED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'menu_toggled'
    });
  };

  const trackAuthOneTapPromptCompleted = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_ONE_TAP_PROMPT_COMPLETED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'one_tap_prompt_completed',
      auth_flow: payload.authFlow || payload.auth_flow || 'one_tap_login',
      method: payload.method || 'google_one_tap',
      action_status: payload.actionStatus || payload.action_status
    });
  };

  const trackAuthOneTapSelected = (payload = {}) => {
    trackEvent(AUTH_EVENTS.AUTH_ONE_TAP_SELECTED, {
      ...withAuthDefaults(payload),
      event_label: payload.eventLabel || payload.event_label || 'one_tap_selected',
      auth_flow: payload.authFlow || payload.auth_flow || 'one_tap_login',
      method: payload.method || 'google_one_tap'
    });
  };

  return {
    trackAuthEntryClicked,
    trackAuthPageViewed,
    trackAuthModeSwitched,
    trackAuthSubmitStarted,
    trackAuthSubmitCompleted,
    trackAuthOauthStarted,
    trackAuthOauthRedirectStarted,
    trackAuthOauthCallbackCompleted,
    trackAuthEmailConfirmationPromptViewed,
    trackAuthConfirmationEmailResendStarted,
    trackAuthConfirmationEmailResendCompleted,
    trackAuthRedirectCompleted,
    trackAuthSessionCheckCompleted,
    trackAuthLogoutStarted,
    trackAuthLogoutCompleted,
    trackAuthUserMenuToggled,
    trackAuthOneTapPromptCompleted,
    trackAuthOneTapSelected
  };
}
